import { computed, onMounted } from 'vue';
import { uniq } from 'lodash';
import { useI18n } from '@/composables';

type FlowContainsValuesConditionEntry = {
  predicate: string;
  values: string[];
};

type FlowConditions = Record<string, FlowContainsValuesConditionEntry>;

export const useFlowContainsValuesCondition = (
  conditions: FlowConditions,
  conditionKey: string,
) => {
  const { t } = useI18n();

  const emptyDisplayValue = computed(() => `[${t('Empty Value')}]`);

  const tabs = computed(() => [
    {
      label: t('From List'),
      key: 'fromList',
      icon: 'fas fa-list',
    },
    {
      label: t('Custom'),
      key: 'custom',
      icon: 'fas fa-pen',
    },
  ]);

  const ensureCondition = () => {
    if (!conditions[conditionKey]) {
      conditions[conditionKey] = {
        predicate: 'CONTAINS',
        values: [],
      };
    }
  };

  const predicate = computed({
    get: () => conditions[conditionKey]?.predicate || 'CONTAINS',
    set: (value: string) => {
      ensureCondition();
      conditions[conditionKey].predicate = value;
    },
  });

  const selected = computed({
    get: () => {
      if (!conditions[conditionKey]) return [];
      return conditions[conditionKey].values.map((value) =>
        !value ? emptyDisplayValue.value : value,
      );
    },
    set: (value: string[]) => {
      ensureCondition();
      conditions[conditionKey].values = value.map((item) =>
        item === emptyDisplayValue.value ? '' : item,
      );
    },
  });

  const includeEmpty = computed({
    get: () => conditions[conditionKey]?.values.some((value) => !value) ?? false,
    set: (value: boolean) => {
      ensureCondition();
      if (value) {
        conditions[conditionKey].values.push('');
      } else {
        const index = conditions[conditionKey].values.indexOf('');
        if (index !== -1) {
          conditions[conditionKey].values.splice(index, 1);
        }
      }
    },
  });

  const customValuesText = computed(() => selected.value.join('\n'));

  const changeCustomField = (event: FocusEvent) => {
    const target = event.target as HTMLTextAreaElement;
    selected.value = uniq(target.value.split('\n').filter((item) => !!item));
  };

  onMounted(() => {
    ensureCondition();
    if (conditions[conditionKey].predicate) {
      predicate.value = conditions[conditionKey].predicate;
    }
  });

  return {
    t,
    tabs,
    predicate,
    selected,
    includeEmpty,
    customValuesText,
    changeCustomField,
  };
};
